import React from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Form,
  Card,
  Button,
  Spinner,
  Alert,
  Badge,
  Pagination,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import { FaMoon, FaSun } from "react-icons/fa";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";

function App() {
  // Load persisted state from localStorage
  const loadPersistedState = (key, defaultValue) => {
    try {
      const savedValue = localStorage.getItem(`aws-region-compare-${key}`);
      return savedValue !== null ? JSON.parse(savedValue) : defaultValue;
    } catch (error) {
      console.error(`Error loading state for ${key}:`, error);
      return defaultValue;
    }
  };

  // Theme state for dark mode toggle
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      // First try to get from localStorage
      const savedMode = localStorage.getItem('aws-region-compare-darkMode');
      if (savedMode !== null) {
        return JSON.parse(savedMode);
      }
      
      // If not in localStorage, check for system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return true;
      }
    } catch (err) {
      console.error("Error setting initial theme mode:", err);
    }
    
    // Default to light mode
    return false;
  });
  
  // State to track window width for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Update window width when resized
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Toggle between light and dark mode
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('aws-region-compare-darkMode', JSON.stringify(newMode));
      // Add a subtle animation effect when toggling
      document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
      return newMode;
    });
  }, []);
  
  // Apply theme class to body element
  useEffect(() => {
    try {
      if (isDarkMode) {
        document.body.classList.add('dark-theme');
        document.body.classList.remove('light-theme');
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
          themeColorMeta.setAttribute('content', '#121212');
        }
        
        // Apply dark theme styles to React-Select global
        document.documentElement.style.setProperty('--react-select-bg', '#1e1e1e');
        document.documentElement.style.setProperty('--react-select-text', '#e0e0e0');
        document.documentElement.style.setProperty('--react-select-border', '#424242');
        
        // Inject direct style overrides for Bootstrap components
        const styleTag = document.getElementById('dark-mode-styles') || document.createElement('style');
        styleTag.id = 'dark-mode-styles';
        styleTag.textContent = `
          .card, .modal-content, .dropdown-menu { background-color: #1e1e1e !important; color: #e0e0e0 !important; border-color: #424242 !important; }
          .card-header, .modal-header, .list-group-item { background-color: #2d2d2d !important; color: #e0e0e0 !important; border-color: #424242 !important; }
          .form-control, .form-select, input, select, textarea { background-color: #1e1e1e !important; color: #e0e0e0 !important; border-color: #424242 !important; }
          .table { color: #e0e0e0 !important; background-color: #1e1e1e !important; border-color: #424242 !important; }
          .table-striped > tbody > tr:nth-of-type(odd) { background-color: #1a1a1a !important; }
          .table-striped > tbody > tr:nth-of-type(even) { background-color: #1e1e1e !important; }
          .table td, .table th { border-color: #424242 !important; }
          .table thead th { background-color: #2d2d2d !important; color: #e0e0e0 !important; border-color: #424242 !important; }
          .pagination .page-link { background-color: #1e1e1e !important; border-color: #424242 !important; color: #e0e0e0 !important; }
          .pagination .page-item.active .page-link { background-color: #5c6bc0 !important; border-color: #5c6bc0 !important; color: white !important; }
          .badge.bg-light, .badge.bg-secondary { color: #e0e0e0 !important; }
          .text-muted { color: #bdbdbd !important; }
          .btn-outline-secondary { color: #bdbdbd !important; border-color: #424242 !important; }
          .btn-outline-secondary:hover { background-color: #333333 !important; color: #e0e0e0 !important; }
          .dropdown-item:hover, .dropdown-item:focus { background-color: #333333 !important; color: #e0e0e0 !important; }
          .table-hover tbody tr:hover { background-color: #333333 !important; }
          .sticky-column { background-color: #1e1e1e !important; }
          tr:nth-of-type(odd) .sticky-column { background-color: #1a1a1a !important; }
          tr:nth-of-type(even) .sticky-column { background-color: #1e1e1e !important; }
          tr:hover .sticky-column { background-color: #333333 !important; }
          .service-name { color: #e0e0e0 !important; }
          .service-name i { color: #5c6bc0 !important; }
          .service-text { color: #e0e0e0 !important; }
          .availability-symbol.available-symbol { color: #66bb6a !important; background-color: rgba(102, 187, 106, 0.1) !important; border-color: rgba(102, 187, 106, 0.2) !important; }
          .items-per-page, .items-per-page-container select { background-color: #1e1e1e !important; color: #e0e0e0 !important; border-color: #424242 !important; }
          .items-per-page option, .items-per-page-container select option { background-color: #1e1e1e !important; color: #e0e0e0 !important; }
          .items-per-page-container, .items-per-page-container span, .items-per-page-container i { color: #e0e0e0 !important; }
          .pagination-container .items-per-page-container { color: #e0e0e0 !important; }
          .pagination-container .items-per-page { background-color: #1e1e1e !important; color: #e0e0e0 !important; border-color: #424242 !important; }
          .availability-symbol.unavailable-symbol { color: #e57373 !important; background-color: rgba(229, 115, 115, 0.1) !important; border-color: rgba(229, 115, 115, 0.2) !important; }
          .form-select { background-color: #1e1e1e !important; color: #e0e0e0 !important; border-color: #424242 !important; appearance: auto !important; }
          .filter-label { color: #e0e0e0 !important; }
          .reset-button { background-color: transparent !important; color: #e0e0e0 !important; border-color: #424242 !important; }
          .reset-button:hover { background-color: rgba(92, 107, 192, 0.2) !important; }
          .reset-button i { color: #e57373 !important; }
          .alert { background-color: rgba(66, 165, 245, 0.15) !important; border-color: rgba(66, 165, 245, 0.3) !important; color: #e0e0e0 !important; }
          .alert i { color: #42a5f5 !important; }
          .form-select option { background-color: #1e1e1e !important; color: #e0e0e0 !important; }
          .form-select:focus { border-color: #5c6bc0 !important; box-shadow: 0 0 0 0.25rem rgba(92, 107, 192, 0.25) !important; }
          select.availability-filter { color: #e0e0e0 !important; background-color: #1e1e1e !important; }
          select.availability-filter option { color: #e0e0e0 !important; background-color: #1e1e1e !important; }
        `;
        // Force dark styles on all select elements
        setTimeout(() => {
          const selectElements = document.querySelectorAll('select, select option');
          selectElements.forEach(select => {
            select.style.backgroundColor = '#1e1e1e';
            select.style.color = '#e0e0e0';
            select.style.borderColor = '#424242';
          });
          
          // Force availability filter to use dark styles
          const availabilityFilter = document.querySelector('.availability-filter');
          if (availabilityFilter) {
            availabilityFilter.style.backgroundColor = '#1e1e1e';
            availabilityFilter.style.color = '#e0e0e0';
            Array.from(availabilityFilter.options).forEach(option => {
              option.style.backgroundColor = '#1e1e1e';
              option.style.color = '#e0e0e0';
            });
          }
        }, 100);
        
        document.head.appendChild(styleTag);
      } else {
        document.body.classList.add('light-theme');
        document.body.classList.remove('dark-theme');
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
          themeColorMeta.setAttribute('content', '#ffffff');
        }
        
        // Reset React-Select global styles
        document.documentElement.style.setProperty('--react-select-bg', '#ffffff');
        document.documentElement.style.setProperty('--react-select-text', '#212121');
        document.documentElement.style.setProperty('--react-select-border', '#e0e0e0');
        
        // Remove dark mode style overrides
        const styleTag = document.getElementById('dark-mode-styles');
        if (styleTag) styleTag.remove();
      }
      
      // Force browser to recalculate styles
      document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
      
      // Immediately force a reflow to ensure styles are applied
      void document.body.offsetHeight;
    } catch (err) {
      console.error("Error applying theme:", err);
    }
    
    return () => {
      // Cleanup
      document.body.classList.remove('dark-theme', 'light-theme');
      const styleTag = document.getElementById('dark-mode-styles');
      if (styleTag) styleTag.remove();
    };
  }, [isDarkMode]);

  // Clear all persisted state
  const clearAllPersistedState = useCallback(() => {
    try {
      // Only clear keys that belong to this app
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("aws-region-compare-")) {
          localStorage.removeItem(key);
        }
      });
      // Reset all state values to defaults
      setSelectedRegions([]);
      setFilter("all");
      setSelectedServices([]);
      setCurrentPage(1);
      setItemsPerPage(20);

      // Show confirmation to user
      alert("All settings have been reset to defaults.");

      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error clearing persisted state:", error);
      alert("Failed to reset settings. Please try again.");
    }
  }, []);

  const [regions, setRegions] = useState([]);
  const [serviceData, setServiceData] = useState({});
  const [selectedRegions, setSelectedRegions] = useState(
    loadPersistedState("selectedRegions", []),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(loadPersistedState("filter", "all")); // 'all', 'available', 'unavailable'
  const [selectedServices, setSelectedServices] = useState(
    loadPersistedState("selectedServices", []),
  );
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentPage, setCurrentPage] = useState(
    loadPersistedState("currentPage", 1),
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    loadPersistedState("itemsPerPage", 20),
  );
  const [portalTarget, setPortalTarget] = useState(null);
  const [regionsMenuOpen, setRegionsMenuOpen] = useState(false);
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const tableContainerRef = useRef(null);

  // Set portal target for dropdown after component mounts
  useEffect(() => {
    if (typeof document !== "undefined") {
      setPortalTarget(document.body);
    }
  }, []);

  // Handle scroll events for visual enhancements
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    const handleScroll = () => {
      const scrollTop = tableContainer.scrollTop;
      const scrollLeft = tableContainer.scrollLeft;
      const header = tableContainer.querySelector(".sticky-header");
      const indicator = tableContainer.querySelector(".table-scroll-indicator");
      const stickyColumns = tableContainer.querySelectorAll(
        "tbody .sticky-column",
      );
      const serviceHeader = tableContainer.querySelector(".service-header");
      const allHeaderCells = tableContainer.querySelectorAll(
        "th:not(.sticky-column)",
      );

      // Add or remove 'is-sticky' class based on scroll position
      if (scrollTop > 5) {
        header?.classList.add("is-sticky");
        indicator?.classList.add("visible");
        serviceHeader?.classList.add("scrolled");
      } else {
        header?.classList.remove("is-sticky");
        indicator?.classList.remove("visible");
        serviceHeader?.classList.remove("scrolled");
      }

      // Fix for non-sticky columns in the header
      if (allHeaderCells && scrollTop > 0) {
        allHeaderCells.forEach((cell) => {
          cell.style.transform = `translateY(${scrollTop}px)`;
          cell.style.position = "relative";
          cell.style.zIndex = "20";
        });
      } else if (allHeaderCells) {
        allHeaderCells.forEach((cell) => {
          cell.style.transform = "";
          cell.style.position = "";
        });
      }

      // Ensure sticky columns in body stay in place when scrolling horizontally
      if (stickyColumns) {
        stickyColumns.forEach((col) => {
          col.style.transform = `translateX(${scrollLeft}px)`;
          col.style.left = "0";
        });
      }

      // Handle the service name header cell specifically (corner cell)
      if (serviceHeader) {
        // Reset any previous transforms
        serviceHeader.style.position = "sticky";
        serviceHeader.style.left = "0";
        serviceHeader.style.top = "0";
        serviceHeader.style.zIndex = "100";

        // Apply necessary styles based on scroll
        if (scrollTop > 0 && scrollLeft > 0) {
          // When scrolling both directions
          serviceHeader.style.boxShadow = "4px 4px 8px rgba(0,0,0,0.2)";
          serviceHeader.style.backgroundColor = "var(--primary-light)";
        } else if (scrollTop > 0) {
          // When scrolling vertically only
          serviceHeader.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
          serviceHeader.style.backgroundColor = "var(--primary-light)";
        } else if (scrollLeft > 0) {
          // When scrolling horizontally only
          serviceHeader.style.boxShadow = "4px 0 8px rgba(0,0,0,0.15)";
          serviceHeader.style.backgroundColor = "var(--primary-light)";
        } else {
          serviceHeader.style.boxShadow = "";
          serviceHeader.style.backgroundColor = "var(--primary-light)";
        }
      }
    };

    // Optimization: Use requestAnimationFrame for smoother scrolling
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Initial call to set up the scroll state
    handleScroll();

    // Add the event listener with performance optimization
    tableContainer.addEventListener("scroll", scrollHandler, { passive: true });

    // Also handle window resize events to recalculate positions
    window.addEventListener("resize", scrollHandler, { passive: true });

    // Force refresh on tab visibility change to fix potential rendering issues
    document.addEventListener("visibilitychange", handleScroll);

    return () => {
      tableContainer.removeEventListener("scroll", scrollHandler);
      window.removeEventListener("resize", scrollHandler);
      document.removeEventListener("visibilitychange", handleScroll);
    };
  }, []);

  // Debug: Monitor serviceData state changes
  useEffect(() => {
    console.log('🔄 serviceData state changed:', {
      hasData: Object.keys(serviceData).length > 0,
      regionCount: Object.keys(serviceData).length,
      sampleRegion: serviceData['us-east-1'] ? Object.keys(serviceData['us-east-1']).length : 'N/A'
    });
  }, [serviceData]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);

        // Get API base URL from environment
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';

        // Fetch regions
        const regionsRes = await axios.get(`${apiBaseUrl}/api/regions`);
        if (!isMounted) return;
        setRegions(Array.isArray(regionsRes.data) ? regionsRes.data : []);

        // Fetch service data
        const servicesRes = await axios.get(`${apiBaseUrl}/api/services`);
        if (!isMounted) return;

        // Transform the service data from Workers API format to expected format
        const rawServiceData = servicesRes.data?.services || [];
        const transformedServiceData = {};
        
        // Check if service data is empty (cache might be refreshing)
        if (rawServiceData.length === 0) {
          console.warn("Service data is empty, cache might be refreshing");
          // Set a message for the user
          setError("Service data is being refreshed. Please wait a moment and try again.");
          setLoading(false);
          return;
        }
        
        // Transform from Workers format to expected format
        rawServiceData.forEach(service => {
          const serviceName = service.serviceName;
          Object.entries(service.regions || {}).forEach(([regionName, regionData]) => {
            if (!transformedServiceData[regionName]) {
              transformedServiceData[regionName] = {};
            }
            transformedServiceData[regionName][serviceName] = regionData.available === true;
          });
        });
        
        // Debug logging
        console.log('🔍 DEBUG: Service Data Transformation');
        console.log(`📊 Transformed ${rawServiceData.length} services`);
        console.log(`🌍 Total regions in transformed data: ${Object.keys(transformedServiceData).length}`);
        
        // Check us-east-1 specifically
        const usEast1Data = transformedServiceData['us-east-1'];
        if (usEast1Data) {
          const availableInUsEast1 = Object.entries(usEast1Data).filter(([service, available]) => available);
          console.log(`🇺🇸 us-east-1: ${availableInUsEast1.length}/${Object.keys(usEast1Data).length} services available`);
          console.log('📝 First 10 available services in us-east-1:', availableInUsEast1.slice(0, 10).map(([service]) => service));
        } else {
          console.error('❌ us-east-1 data is missing!');
        }
        
        // Check if any services are available anywhere
        const totalAvailable = Object.values(transformedServiceData).reduce((total, regionData) => {
          return total + Object.values(regionData).filter(Boolean).length;
        }, 0);
        console.log(`📈 Total available service instances: ${totalAvailable}`);
        
        // Sample data structure
        console.log('🔬 Sample transformed data structure:', {
          'us-east-1': Object.fromEntries(Object.entries(usEast1Data || {}).slice(0, 3)),
          'us-west-2': Object.fromEntries(Object.entries(transformedServiceData['us-west-2'] || {}).slice(0, 3))
        });
        
        setServiceData(transformedServiceData);

        // Debug: Check state after setting
        console.log('🔄 State update: serviceData set');
        
        // Make sure lastUpdated is a valid date
        const lastUpdatedDate = servicesRes.data?.lastUpdated
          ? new Date(servicesRes.data.lastUpdated)
          : new Date();

        setLastUpdated(
          isNaN(lastUpdatedDate.getTime()) ? new Date() : lastUpdatedDate,
        );

        setLoading(false);
        console.log('✅ Data loading completed successfully');
      } catch (err) {
        if (!isMounted) return;
        console.error("Error fetching data:", err);
        
        // Provide more specific error messages
        if (err.response?.status === 500) {
          setError("Server error occurred. The service data might be refreshing. Please try again in a few minutes.");
        } else if (err.response?.status === 401) {
          setError("Authentication error. Please contact support.");
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          setError("Network error. Please check your connection and try again.");
        } else {
          setError("Failed to load data. Please try again later.");
        }
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Save state to localStorage
  const saveState = useCallback((key, value) => {
    try {
      // Check if localStorage is available
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          `aws-region-compare-${key}`,
          JSON.stringify(value),
        );
      }
    } catch (error) {
      // Handle quota exceeded or other storage errors
      console.error(`Error saving state for ${key}:`, error);

      // If it's a quota error, try to clear some space
      if (
        error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
        error.code === 22
      ) {
        console.warn("Storage quota exceeded, attempting to clear old items");
        try {
          // Clear only our app's data that might be less important
          localStorage.removeItem(`aws-region-compare-filter`);
          localStorage.removeItem(`aws-region-compare-currentPage`);
          localStorage.removeItem(`aws-region-compare-itemsPerPage`);

          // Try saving again after clearing
          localStorage.setItem(
            `aws-region-compare-${key}`,
            JSON.stringify(value),
          );
        } catch (retryError) {
          console.error(
            "Still unable to save state after clearing space:",
            retryError,
          );
        }
      }
    }
  }, []);

  // Handle region selection (max 5)
  const handleRegionSelect = useCallback(
    (selectedOptions) => {
      try {
        if (!selectedOptions) {
          setSelectedRegions([]);
          saveState("selectedRegions", []);
          return;
        }

        // Ensure selectedOptions is an array
        const optionsArray = Array.isArray(selectedOptions)
          ? selectedOptions
          : [];

        // Enforce max 5 regions limit
        const newRegions = optionsArray
          .filter(
            (option) =>
              option && typeof option === "object" && "value" in option,
          )
          .map((option) => option.value)
          .slice(0, 5);

        setSelectedRegions(newRegions);
        saveState("selectedRegions", newRegions);

        // Reset page when changing regions
        setCurrentPage(1);
        saveState("currentPage", 1);
      } catch (error) {
        console.error("Error in handleRegionSelect:", error);
        setSelectedRegions([]);
        saveState("selectedRegions", []);
      }
    },
    [saveState],
  );

  // Clear all selected regions
  const clearSelectedRegions = () => {
    setSelectedRegions([]);
    saveState("selectedRegions", []);
    setCurrentPage(1);
    saveState("currentPage", 1);
  };

  // Format regions for react-select
  const regionOptions = useMemo(() => {
    if (!regions || !Array.isArray(regions)) {
      return [];
    }
    return regions.map((region) => ({
      value: region.RegionName,
      label: region.RegionName,
    }));
  }, [regions]);



  // Handle service selection for filtering
  const handleServiceSelect = useCallback(
    (selectedOptions) => {
      try {
        if (!selectedOptions) {
          setSelectedServices([]);
          saveState("selectedServices", []);
          return;
        }

        // Ensure selectedOptions is an array
        const optionsArray = Array.isArray(selectedOptions)
          ? selectedOptions
          : [];
        const selectedValues = optionsArray
          .filter(
            (option) =>
              option && typeof option === "object" && "value" in option,
          )
          .map((option) => option.value);
        setSelectedServices(selectedValues);
        saveState("selectedServices", selectedValues);
      } catch (error) {
        console.error("Error in handleServiceSelect:", error);
        setSelectedServices([]);
        saveState("selectedServices", []);
      }
    },
    [saveState],
  );

  // Get services array (memoized)
  const availableServices = useMemo(() => {
    // Guard against null or undefined serviceData
    if (
      !serviceData ||
      typeof serviceData !== "object" ||
      Object.keys(serviceData).length === 0
    ) {
      return [];
    }

    try {
      // Get all service names from selected regions
      let allServices = new Set();

      // Ensure selectedRegions is an array before iterating
      if (Array.isArray(selectedRegions)) {
        selectedRegions.forEach((region) => {
          if (
            region &&
            serviceData[region] &&
            typeof serviceData[region] === "object"
          ) {
            Object.keys(serviceData[region]).forEach((service) => {
              if (service) allServices.add(service);
            });
          }
        });
      }

      // Include all AWS services from all regions
      Object.keys(serviceData).forEach((region) => {
        if (
          region &&
          serviceData[region] &&
          typeof serviceData[region] === "object"
        ) {
          Object.keys(serviceData[region]).forEach((service) => {
            if (service) allServices.add(service);
          });
        }
      });

      const sortedServices = Array.from(allServices).filter(Boolean).sort();
      return sortedServices;
    } catch (error) {
      console.error("Error processing service data:", error);
      return [];
    }
  }, [serviceData, selectedRegions]);

  // Format services for react-select with optimized loading
  const serviceOptions = useMemo(() => {
    if (!availableServices || !Array.isArray(availableServices)) {
      console.log("Available services is not a valid array");
      return [];
    }
    console.log(`Total services available: ${availableServices.length}`);
    return availableServices.map((service) => ({
      value: service,
      label: service,
    }));
  }, [availableServices]);



  // Memoized filtered services calculation
  const filteredServices = useMemo(() => {
    if (
      loading ||
      !serviceData ||
      !Array.isArray(availableServices) ||
      availableServices.length === 0
    ) {
      return [];
    }

    try {
      // First apply the availability filter
      let result = [...availableServices];

      if (
        filter !== "all" &&
        Array.isArray(selectedRegions) &&
        selectedRegions.length > 0
      ) {
        result = availableServices.filter((service) => {
          if (!service) return false;

          const isAvailableInAll = selectedRegions.every((region) => {
            return (
              region &&
              serviceData[region] &&
              typeof serviceData[region] === "object" &&
              serviceData[region][service] === true
            );
          });

          if (filter === "available") return isAvailableInAll;
          if (filter === "unavailable") return !isAvailableInAll;
          return true;
        });
      }

      // Then filter by selected services if any are selected
      if (Array.isArray(selectedServices) && selectedServices.length > 0) {
        return result.filter(
          (service) => service && selectedServices.includes(service),
        );
      }

      return result;
    } catch (error) {
      console.error("Error filtering services:", error);
      return [];
    }
  }, [
    availableServices,
    serviceData,
    selectedRegions,
    selectedServices,
    filter,
    loading,
  ]);

  // Get paginated subset of filtered services
  const paginatedServices = useMemo(() => {
    if (!Array.isArray(filteredServices) || filteredServices.length === 0) {
      return [];
    }

    try {
      const page = Number(currentPage) || 1;
      const perPage = Number(itemsPerPage) || 20;

      // Ensure we have valid numeric values
      if (isNaN(page) || isNaN(perPage) || page < 1 || perPage < 1) {
        console.warn("Invalid pagination parameters:", { page, perPage });
        return filteredServices.slice(0, 20); // Default to first 20 items
      }

      const startIndex = (page - 1) * perPage;
      return filteredServices.slice(startIndex, startIndex + perPage);
    } catch (error) {
      console.error("Error paginating services:", error);
      return [];
    }
  }, [filteredServices, currentPage, itemsPerPage]);

  // Calculate total number of pages
  const totalPages = useMemo(() => {
    if (!filteredServices || !Array.isArray(filteredServices)) {
      return 1;
    }
    const pages = Math.max(
      1,
      Math.ceil(filteredServices.length / itemsPerPage),
    );
    return isFinite(pages) ? pages : 1;
  }, [filteredServices, itemsPerPage]);

  // Handle page change
  const handlePageChange = useCallback(
    (pageNumber) => {
      // Ensure page number is within valid range
      if (pageNumber < 1) pageNumber = 1;
      if (pageNumber > totalPages) pageNumber = totalPages;
      setCurrentPage(pageNumber);
      saveState("currentPage", pageNumber);

      // Scroll to top of table when changing pages
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [totalPages, saveState],
  );

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    try {
      const value = Number(e.target.value);
      if (!isNaN(value) && value > 0) {
        setItemsPerPage(value);
        saveState("itemsPerPage", value);
        setCurrentPage(1); // Reset to first page when changing items per page
        saveState("currentPage", 1);

        // Scroll to top when changing items per page
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    } catch (error) {
      console.error("Error in handleItemsPerPageChange:", error);
      setItemsPerPage(20); // Default fallback
      saveState("itemsPerPage", 20);
    }
  };



  // Card and UI component styles based on theme
  const cardStyle = {
    backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
    color: isDarkMode ? '#e0e0e0' : '#212121',
    borderColor: isDarkMode ? '#424242' : '#e0e0e0'
  };
  
  const cardHeaderStyle = {
    backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
    color: isDarkMode ? '#e0e0e0' : '#212121',
    borderColor: isDarkMode ? '#424242' : '#e0e0e0'
  };
  

  
  return (
    <Container className="py-4" data-domain="awsregioncompare.xyz">
      <header className="app-header mb-4" role="banner" style={{
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        color: isDarkMode ? '#e0e0e0' : '#212121',
        boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
        borderColor: isDarkMode ? '#424242' : '#e0e0e0'
      }}>
        <div className="theme-toggle-container">
          <button 
            className="theme-toggle-btn" 
            onClick={toggleTheme} 
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
            }}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              backgroundColor: isDarkMode ? '#3949ab' : '#e8eaf6',
              color: isDarkMode ? '#ffffff' : '#303f9f',
              border: `1px solid ${isDarkMode ? '#5c6bc0' : '#3f51b5'}`,
              borderRadius: '20px',
              padding: '8px 12px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              zIndex: 100,
              minHeight: '44px',
              fontWeight: 'bold',
              ':hover': {
                backgroundColor: isDarkMode ? '#4252b5' : '#d0d4f2',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }
            }}
          >
            {isDarkMode ? (
              <><FaSun style={{color: '#ffa726', fontSize: '1.2rem', marginRight: '4px'}} /> <span style={{display: windowWidth <= 576 ? 'none' : 'inline'}}>Light Mode</span></>
            ) : (
              <><FaMoon style={{color: '#303f9f', fontSize: '1.2rem', marginRight: '4px'}} /> <span style={{display: windowWidth <= 576 ? 'none' : 'inline'}}>Dark Mode</span></>
            )}
          </button>
        </div>
        <div className="app-logo">
          <i className="bi bi-cloud-fill" aria-hidden="true"></i>
          <div>
            <h1 className="text-center">AWS Region Service Comparison</h1>
            <p className="text-center text-muted" style={{ color: isDarkMode ? '#bdbdbd' : '#6c757d' }}>
              Compare AWS service availability across different regions
            </p>
          </div>
        </div>

        <div className="d-flex justify-content-center align-items-center">
          {lastUpdated && (
            <div className="text-center update-info me-3">
              <i className="bi bi-clock-history me-1" aria-hidden="true"></i>
              <small className="text-muted">
                Last data update: {lastUpdated.toLocaleString()}
              </small>
            </div>
          )}

          <Button
          variant="outline-secondary"
          size="sm"
          className={`reset-button ${isDarkMode ? 'dark-theme-button' : ''}`}
          onClick={clearAllPersistedState}
          title="Reset all selections and filters to default values"
          aria-label="Reset all settings"
          style={{
            backgroundColor: 'transparent',
            color: isDarkMode ? '#e0e0e0' : '#6c757d',
            borderColor: isDarkMode ? '#424242' : '#dee2e6',
            padding: '0.4rem 0.75rem',
            transition: 'all 0.3s ease'
          }}
        >
            <i
              className="bi bi-arrow-counterclockwise"
              aria-hidden="true"
              style={{ color: isDarkMode ? '#e57373' : '#dc3545' }}
            ></i>{" "}
            Reset All Settings
          </Button>
        </div>
      </header>

      {error && (
        <Alert variant="danger" role="alert">
          {error}
        </Alert>
      )}

      {loading ? (
        <main className="text-center p-5 loading-container">
          <div className="loading-indicator">
            <Spinner animation="border" aria-label="Loading" role="status" />
            <div className="mt-3">
              <i className="bi bi-cloud-arrow-down me-2" aria-hidden="true"></i>
              <p className="d-inline">Loading AWS region data...</p>
            </div>
          </div>
        </main>
      ) : (
        <main>
          <section className="mb-4 region-selection-card">
            <Card style={cardStyle}>
              <Card.Header className="d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                <h2 className="h5">
                  <span className="step-number" aria-hidden="true">
                    1
                  </span>{" "}
                  Select regions to compare
                  <span className="text-muted ms-2">(max 5)</span>
                </h2>
                {selectedRegions.length > 0 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={clearSelectedRegions}
                    className="clear-services-btn"
                    style={{
                      backgroundColor: 'transparent',
                      color: isDarkMode ? '#bdbdbd' : '#757575',
                      borderColor: isDarkMode ? '#424242' : '#e0e0e0'
                    }}
                  >
                    <span className="me-1">Clear All</span>
                    <span 
                      className="badge bg-primary"
                      style={{ 
                        backgroundColor: isDarkMode ? '#5c6bc0' : '#3f51b5',
                        color: 'white'
                      }}
                    >
                      {selectedRegions.length}
                    </span>
                  </Button>
                )}
              </Card.Header>
              <Card.Body style={cardStyle}>
                <div
                  className="select-wrapper"
                  style={{ position: "relative", zIndex: 1003, width: "100%" }}
                >
                  <Select
                    isMulti
                    options={regionOptions}
                    onChange={handleRegionSelect}
                    placeholder={
                      loading
                        ? "Loading regions..."
                        : "Select up to 5 regions..."
                    }
                    classNamePrefix="react-select"
                    theme={theme => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: isDarkMode ? '#5c6bc0' : '#3f51b5',
                        primary75: isDarkMode ? '#4d5cad' : '#3949ab',
                        primary50: isDarkMode ? '#3f4c99' : '#303f9f',
                        primary25: isDarkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(63, 81, 181, 0.1)',
                        neutral0: isDarkMode ? '#1e1e1e' : '#ffffff',
                        neutral5: isDarkMode ? '#2a2a2a' : '#f8f9fa',
                        neutral10: isDarkMode ? '#333333' : '#f1f3f5',
                        neutral20: isDarkMode ? '#424242' : '#e0e0e0',
                        neutral30: isDarkMode ? '#5c5c5c' : '#ced4da',
                        neutral40: isDarkMode ? '#999999' : '#adb5bd',
                        neutral50: isDarkMode ? '#bdbdbd' : '#6c757d',
                        neutral60: isDarkMode ? '#cccccc' : '#6c757d',
                        neutral70: isDarkMode ? '#d9d9d9' : '#495057',
                        neutral80: isDarkMode ? '#e0e0e0' : '#343a40',
                        neutral90: isDarkMode ? '#f5f5f5' : '#212529',
                      }
                    })}
                    value={
                      Array.isArray(selectedRegions)
                        ? selectedRegions.map((region) => ({
                            value: region || "",
                            label: region || "",
                          }))
                        : []
                    }
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        fontSize: "0.875rem",
                        zIndex: regionsMenuOpen ? 1004 : 1001,
                        borderColor: regionsMenuOpen
                          ? "#86b7fe"
                          : baseStyles.borderColor,
                        boxShadow: regionsMenuOpen
                          ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                          : baseStyles.boxShadow,
                        backgroundColor: regionsMenuOpen
                          ? "#f8f9fa"
                          : baseStyles.backgroundColor,
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        maxHeight: "300px",
                        zIndex: 9999,
                      }),
                      menuList: (baseStyles) => ({
                        ...baseStyles,
                        maxHeight: "300px",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      multiValue: (styles) => ({
                        ...styles,
                        backgroundColor: "#cfe2ff",
                        border: "1px solid #b6d4fe",
                      }),
                      multiValueLabel: (styles) => ({
                        ...styles,
                        color: "#0a58ca",
                        fontWeight: 500,
                      }),
                      multiValueRemove: (styles) => ({
                        ...styles,
                        color: "#0a58ca",
                        ":hover": {
                          backgroundColor: "#dc3545",
                          color: "white",
                        },
                      }),
                    }}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    isClearable={true}
                    pageSize={100}
                    maxMenuHeight={300}
                    isDisabled={loading}
                    noOptionsMessage={() => "No regions found"}
                    loadingMessage={() => "Loading regions..."}
                    menuPortalTarget={portalTarget}
                    menuPosition={"fixed"}
                    isOptionDisabled={() => selectedRegions.length >= 5}
                    onMenuOpen={() => setRegionsMenuOpen(true)}
                    onMenuClose={() => setRegionsMenuOpen(false)}
                    formatOptionLabel={({ value, label }) => (
                      <div>
                        <span className="me-1">{label}</span>
                        {selectedRegions.length >= 5 &&
                          !selectedRegions.includes(value) && (
                            <span className="text-muted">(limit reached)</span>
                          )}
                      </div>
                    )}
                  />
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i> Type to search
                    regions
                  </small>
                  {selectedRegions.length > 0 && (
                    <Badge bg="primary" className="ms-2 regions-badge">
                      {selectedRegions.length}/5 regions selected
                    </Badge>
                  )}
                </div>
              </Card.Body>
            </Card>
          </section>

          {selectedRegions.length === 1 && (
            <Alert
              variant="info"
              className="mb-4 instruction-alert"
              role="alert"
              style={{
                backgroundColor: isDarkMode ? 'rgba(66, 165, 245, 0.15)' : '#cfe2ff',
                borderColor: isDarkMode ? 'rgba(66, 165, 245, 0.3)' : '#b6d4fe',
                color: isDarkMode ? '#e0e0e0' : '#084298'
              }}
            >
              <i className="bi bi-arrow-up-circle me-2" aria-hidden="true" style={{ color: isDarkMode ? '#42a5f5' : '#084298' }}></i>
              Please select at least one more region to compare services.
            </Alert>
          )}

          {selectedRegions.length === 0 && (
            <Alert
              variant="info"
              className="mb-4 instruction-alert"
              role="alert"
              style={{
                backgroundColor: isDarkMode ? 'rgba(66, 165, 245, 0.15)' : '#cfe2ff',
                borderColor: isDarkMode ? 'rgba(66, 165, 245, 0.3)' : '#b6d4fe',
                color: isDarkMode ? '#e0e0e0' : '#084298'
              }}
            >
              <i className="bi bi-arrow-up-circle me-2" aria-hidden="true" style={{ color: isDarkMode ? '#42a5f5' : '#084298' }}></i>
              Please select at least 2 regions to compare services.
            </Alert>
          )}

          {selectedRegions.length >= 2 && (
            <section className="comparison-section">
              <Card className="comparison-card" style={cardStyle}>
                <Card.Header style={cardHeaderStyle}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h2 className="h5">
                      <span className="step-number" aria-hidden="true">
                        2
                      </span>{" "}
                      Service Comparison
                      <span className="text-muted ms-2">
                        ({filteredServices.length} services)
                      </span>
                    </h2>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-clear-filters">
                          Reset to show all services
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedServices([]);
                          saveState("selectedServices", []);
                          setFilter("all");
                          saveState("filter", "all");
                          setCurrentPage(1);
                          saveState("currentPage", 1);
                        }}
                        disabled={
                          selectedServices.length === 0 && filter === "all"
                        }
                      >
                        <i className="bi bi-x-circle me-1"></i> Clear All
                        Filters
                      </Button>
                    </OverlayTrigger>
                  </div>
                  <Row className="filter-container">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="filter-label" style={{ color: isDarkMode ? '#e0e0e0' : '#212121' }}>
                          <i
                            className="bi bi-filter me-1"
                            aria-hidden="true"
                            style={{ color: isDarkMode ? '#5c6bc0' : '#3f51b5' }}
                          ></i>{" "}
                          Filter by availability:
                        </Form.Label>
                        <Form.Select
                                          size="sm"
                                          value={filter}
                                          onChange={(e) => {
                                            const newFilter = e.target.value;
                                            setFilter(newFilter);
                                            saveState("filter", newFilter);
                                            setCurrentPage(1);
                                            saveState("currentPage", 1);
                            
                                            // Force styles on options after selection
                                            if (isDarkMode) {
                                              setTimeout(() => {
                                                const select = e.target;
                                                select.style.backgroundColor = '#1e1e1e';
                                                select.style.color = '#e0e0e0';
                                                Array.from(select.options).forEach(option => {
                                                  option.style.backgroundColor = '#1e1e1e';
                                                  option.style.color = '#e0e0e0';
                                                });
                                              }, 0);
                                            }
                                          }}
                                          className="availability-filter"
                                          style={{
                                            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
                                            color: isDarkMode ? '#e0e0e0' : '#212121',
                                            borderColor: isDarkMode ? '#424242' : '#ced4da'
                                          }}
                                        >
                          <option value="all">All Services</option>
                          <option value="available">
                            Available in All Selected Regions
                          </option>
                          <option value="unavailable">
                            Unavailable in At Least One Region
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="filter-label" style={{ color: isDarkMode ? '#e0e0e0' : '#212121' }}>
                          <i
                            className="bi bi-search me-1"
                            aria-hidden="true"
                          ></i>{" "}
                          Filter by specific services:
                        </Form.Label>
                        <div
                          className="select-wrapper"
                          style={{ position: "relative", zIndex: 1002, width: "100%" }}
                        >
                          <Select
                            isMulti
                            options={serviceOptions}
                            onChange={handleServiceSelect}
                            placeholder={
                              loading
                                ? "Loading services..."
                                : "Type to search services..."
                            }
                            classNamePrefix="react-select"
                            theme={theme => ({
                              ...theme,
                              colors: {
                                ...theme.colors,
                                primary: isDarkMode ? '#5c6bc0' : '#3f51b5',
                                primary75: isDarkMode ? '#4d5cad' : '#3949ab',
                                primary50: isDarkMode ? '#3f4c99' : '#303f9f',
                                primary25: isDarkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(63, 81, 181, 0.1)',
                                neutral0: isDarkMode ? '#1e1e1e' : '#ffffff',
                                neutral5: isDarkMode ? '#2a2a2a' : '#f8f9fa',
                                neutral10: isDarkMode ? '#333333' : '#f1f3f5',
                                neutral20: isDarkMode ? '#424242' : '#e0e0e0',
                                neutral30: isDarkMode ? '#5c5c5c' : '#ced4da',
                                neutral40: isDarkMode ? '#999999' : '#adb5bd',
                                neutral50: isDarkMode ? '#bdbdbd' : '#6c757d',
                                neutral60: isDarkMode ? '#cccccc' : '#6c757d',
                                neutral70: isDarkMode ? '#d9d9d9' : '#495057',
                                neutral80: isDarkMode ? '#e0e0e0' : '#343a40',
                                neutral90: isDarkMode ? '#f5f5f5' : '#212529',
                              }
                            })}
                            value={
                              Array.isArray(selectedServices)
                                ? selectedServices.map((service) => ({
                                    value: service || "",
                                    label: service || "",
                                  }))
                                : []
                            }
                            styles={{
                              control: (baseStyles) => ({
                                ...baseStyles,
                                fontSize: "0.875rem",
                                zIndex: servicesMenuOpen ? 1004 : 1001,
                                borderColor: servicesMenuOpen
                                  ? "#86b7fe"
                                  : baseStyles.borderColor,
                                boxShadow: servicesMenuOpen
                                  ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                                  : baseStyles.boxShadow,
                                backgroundColor: servicesMenuOpen
                                  ? "#f8f9fa"
                                  : baseStyles.backgroundColor,
                              }),
                              menu: (baseStyles) => ({
                                ...baseStyles,
                                maxHeight: "300px",
                                zIndex: 9999,
                              }),
                              menuList: (baseStyles) => ({
                                ...baseStyles,
                                maxHeight: "300px",
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              multiValue: (styles) => ({
                                ...styles,
                                backgroundColor: "#e9ecef",
                                border: "1px solid #ced4da",
                              }),
                              multiValueLabel: (styles) => ({
                                ...styles,
                                color: "#495057",
                              }),
                              multiValueRemove: (styles) => ({
                                ...styles,
                                ":hover": {
                                  backgroundColor: "#dc3545",
                                  color: "white",
                                },
                              }),
                            }}
                            isSearchable={true}
                            closeMenuOnSelect={false}
                            isClearable={true}
                            pageSize={100}
                            maxMenuHeight={300}
                            isDisabled={loading}
                            noOptionsMessage={() => "No services found"}
                            loadingMessage={() => "Loading services..."}
                            menuPortalTarget={portalTarget}
                            menuPosition={"fixed"}
                            onMenuOpen={() => setServicesMenuOpen(true)}
                            onMenuClose={() => setServicesMenuOpen(false)}
                          />
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <small className="text-muted">
                            <i className="bi bi-info-circle me-1"></i> Type to
                            search services
                          </small>
                          {selectedServices.length > 0 && (
                            <Badge
                              bg="secondary"
                              className="ms-2 services-badge"
                            >
                              {selectedServices.length} service
                              {selectedServices.length !== 1 ? "s" : ""}{" "}
                              selected
                            </Badge>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body style={cardStyle}>
                  <div className="mb-3 d-flex justify-content-between results-summary">
                    <span
                      className="text-muted results-count"
                      aria-live="polite"
                      style={{ color: isDarkMode ? '#bdbdbd' : '#6c757d' }}
                    >
                      <i
                        className="bi bi-check-circle me-1"
                        aria-hidden="true"
                        style={{ color: isDarkMode ? '#66bb6a' : '#4caf50' }}
                      ></i>
                      Showing {filteredServices ? filteredServices.length : 0}{" "}
                      of {availableServices ? availableServices.length : 0}{" "}
                      services
                    </span>
                    {selectedServices.length > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none clear-services-btn"
                        onClick={() => {
                          setSelectedServices([]);
                          saveState("selectedServices", []);
                          setCurrentPage(1);
                          saveState("currentPage", 1);
                        }}
                      >
                        <i className="bi bi-x-circle me-1"></i> Clear service
                        filter
                      </Button>
                    )}
                  </div>
                  <div
                    className="table-responsive"
                    ref={tableContainerRef}
                    style={{
                      zIndex: 1,
                      position: "relative",
                      overflow: "auto",
                    }}
                    role="region"
                    aria-label="AWS Services Comparison Table"
                  >
                    <div
                      className="table-scroll-indicator"
                      aria-hidden="true"
                    ></div>
                    <table
                      className="table table-striped table-bordered table-hover sticky-table"
                      aria-label="AWS Services by Region"
                      style={{
                        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
                        color: isDarkMode ? '#e0e0e0' : '#212121',
                        borderColor: isDarkMode ? '#424242' : '#e0e0e0',
                        borderCollapse: 'separate',
                        borderSpacing: 0
                      }}
                    >
                      <caption className="sr-only">
                        Comparison of AWS services availability across selected
                        regions
                      </caption>
                      <thead
                        className="sticky-header"
                        style={{ 
                          position: "sticky", 
                          top: 0, 
                          zIndex: 15,
                          backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
                          color: isDarkMode ? '#e0e0e0' : '#212121',
                          boxShadow: isDarkMode ? '0 2px 5px rgba(0,0,0,0.3)' : '0 2px 5px rgba(0,0,0,0.1)'
                        }}
                      >
                        <tr>
                          <th
                            className="sticky-column service-header"
                            style={{
                              zIndex: 100,
                              position: "sticky",
                              top: 0,
                              left: 0,
                            }}
                            scope="col"
                          >
                            <div className="d-flex align-items-center">
                              <i
                                className="bi bi-list me-2"
                                aria-hidden="true"
                              ></i>{" "}
                              Service Name
                            </div>
                          </th>
                          {selectedRegions.map((region) => (
                            <th
                              key={region}
                              className="region-header"
                              style={{ position: "sticky", top: 0, zIndex: 20 }}
                              scope="col"
                            >
                              <div className="d-flex align-items-center justify-content-center region-cell">
                                <i
                                  className="bi bi-geo-alt me-1"
                                  aria-hidden="true"
                                ></i>{" "}
                                {region}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {!filteredServices || filteredServices.length === 0 ? (
                          <tr>
                            <td
                              colSpan={selectedRegions.length + 1}
                              className="text-center py-4"
                            >
                              No services match the current filters
                            </td>
                          </tr>
                        ) : (
                          paginatedServices.map((service, rowIndex) => (
                            <tr
                              key={service}
                              className={
                                rowIndex % 2 === 0 ? "even-row" : "odd-row"
                              }
                            >
                              <td
                                className="sticky-column service-name"
                                style={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 14,
                                  backgroundColor: isDarkMode ? service.highlight ? 'rgba(92, 107, 192, 0.15)' : (rowIndex % 2 === 0 ? '#1a1a1a' : '#1e1e1e') : service.highlight ? 'rgba(63, 81, 181, 0.1)' : (rowIndex % 2 === 0 ? '#f8f9fa' : '#ffffff'),
                                  color: isDarkMode ? '#e0e0e0' : '#212121',
                                  borderColor: isDarkMode ? '#424242' : '#e0e0e0'
                                }}
                              >
                                <div className={`service-name ${isDarkMode ? 'dark-theme-service' : ''}`} style={{ color: isDarkMode ? '#e0e0e0' : '#212121' }}>
                                  <i
                                    className="bi bi-hdd-stack me-2"
                                    aria-hidden="true"
                                    style={{ color: isDarkMode ? '#5c6bc0' : '#3f51b5' }}
                                  ></i>
                                  <div className={`service-text ${isDarkMode ? 'dark-theme-text' : ''}`} title={service} style={{ color: isDarkMode ? '#e0e0e0' : '#212121' }}>
                                    {service}
                                  </div>
                                </div>
                              </td>
                              {selectedRegions.map((region) => (
                                <td
                                  key={`${region}-${service}`}
                                  className="text-center availability-cell"
                                >
                                  {serviceData[region] &&
                                  serviceData[region][service] ? (
                                    <span
                                    className={`availability-symbol available-symbol ${isDarkMode ? 'dark-theme-available' : ''}`}
                                    title="Available in this region"
                                    aria-label="Available"
                                    style={{ 
                                      color: isDarkMode ? '#66bb6a' : '#4caf50',
                                      backgroundColor: isDarkMode ? 'rgba(102, 187, 106, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                      borderColor: isDarkMode ? 'rgba(102, 187, 106, 0.2)' : 'rgba(76, 175, 80, 0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                      <i
                                        className="bi bi-check-circle-fill"
                                        aria-hidden="true"
                                      ></i>
                                    </span>
                                  ) : (
                                    <span
                                      className={`availability-symbol unavailable-symbol ${isDarkMode ? 'dark-theme-unavailable' : ''}`}
                                      title="Unavailable in this region"
                                      aria-label="Unavailable"
                                      style={{ 
                                        color: isDarkMode ? '#e57373' : '#f44336',
                                        backgroundColor: isDarkMode ? 'rgba(229, 115, 115, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                        borderColor: isDarkMode ? 'rgba(229, 115, 115, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <i
                                        className="bi bi-x-circle-fill"
                                        aria-hidden="true"
                                      ></i>
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  <div className="d-flex justify-content-between align-items-center mt-4 pagination-container">
                    <div className="d-flex align-items-center items-per-page-container">
                      <span 
                        className="me-2"
                        style={{
                          color: isDarkMode ? '#e0e0e0' : '#212121',
                          transition: 'color 0.3s ease'
                        }}
                      >
                        <i
                          className="bi bi-list-ul me-1"
                          aria-hidden="true"
                          style={{
                            color: isDarkMode ? '#e0e0e0' : '#212121',
                            transition: 'color 0.3s ease'
                          }}
                        ></i>{" "}
                        Services per page:
                      </span>
                      <Form.Select
                        size="sm"
                        className="items-per-page"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        style={{
                          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
                          color: isDarkMode ? '#e0e0e0' : '#212121',
                          borderColor: isDarkMode ? '#424242' : '#e0e0e0',
                          transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease'
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Form.Select>
                    </div>

                    <div className="d-flex align-items-center pagination-controls">
                      <span
                        className="me-3 d-none d-md-block pagination-info"
                        aria-live="polite"
                      >
                        {Array.isArray(filteredServices) &&
                        filteredServices.length > 0 ? (
                          <span>
                            <i
                              className="bi bi-info-circle me-1"
                              aria-hidden="true"
                            ></i>
                            Showing{" "}
                            <strong>
                              {Math.min(
                                (currentPage - 1) * itemsPerPage + 1,
                                filteredServices.length,
                              )}
                            </strong>{" "}
                            to{" "}
                            <strong>
                              {Math.min(
                                currentPage * itemsPerPage,
                                filteredServices.length,
                              )}
                            </strong>{" "}
                            of <strong>{filteredServices.length}</strong>{" "}
                            services
                          </span>
                        ) : (
                          <span>No services to display</span>
                        )}
                      </span>

                      {Array.isArray(filteredServices) &&
                        filteredServices.length > 0 && (
                          <Pagination size="sm" className="mb-0">
                            <Pagination.First
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              style={currentPage === 1 ? { opacity: 0.5 } : null}
                            />
                            <Pagination.Prev
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              style={currentPage === 1 ? { opacity: 0.5 } : null}
                            />

                            {currentPage > 2 && (
                              <Pagination.Item
                                onClick={() => handlePageChange(1)}
                              >
                                1
                              </Pagination.Item>
                            )}
                            {currentPage > 3 && (
                              <Pagination.Ellipsis disabled />
                            )}

                            {currentPage > 1 && (
                              <Pagination.Item
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                              >
                                {currentPage - 1}
                              </Pagination.Item>
                            )}
                            <Pagination.Item active>
                              {currentPage}
                            </Pagination.Item>
                            {currentPage < totalPages && (
                              <Pagination.Item
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                              >
                                {currentPage + 1}
                              </Pagination.Item>
                            )}

                            {currentPage < totalPages - 2 && (
                              <Pagination.Ellipsis disabled />
                            )}
                            {currentPage < totalPages - 1 && (
                              <Pagination.Item
                                onClick={() => handlePageChange(totalPages)}
                              >
                                {totalPages}
                              </Pagination.Item>
                            )}

                            <Pagination.Next
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              style={currentPage === totalPages ? { opacity: 0.5 } : null}
                            />
                            <Pagination.Last
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              style={currentPage === totalPages ? { opacity: 0.5 } : null}
                            />
                          </Pagination>
                        )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </section>
          )}
        </main>
      )}
      <footer className="mt-4 text-center text-muted">
        <small>
          &copy; {new Date().getFullYear()}{" "}
          <a href="https://awsregioncompare.xyz" className="text-muted">
            awsregioncompare.xyz
          </a>{" "}
          |{" "}
          <button className="btn btn-link text-muted p-0" style={{fontSize: 'inherit', textDecoration: 'none'}}>
            Terms
          </button>{" "}
          |{" "}
          <button className="btn btn-link text-muted p-0" style={{fontSize: 'inherit', textDecoration: 'none'}}>
            Privacy
          </button>
        </small>
      </footer>
    </Container>
  );
}

export default App;
